import { memo, useMemo, useState } from 'react'
import shortid from 'shortid'
import { useCookies } from 'react-cookie'
import { isEqual, debounce } from 'lodash'

import { DOUBLE_FACED_LAYOUTS } from 'types/card'
import { Option } from 'components/elements/ArchidektDropdown/types'

import SaveService from 'services/save.service'
import PinService from 'services/pin.service'
import ToastService from 'services/toast.service'

import { useCanEditDeck } from 'redux/deck/selectors'
import { useActionless, useAppSelector } from 'redux/hooks'

import { SET_ACTIVE_STATE } from 'redux/active/actions/types'

import CardModalStandalone from 'components/card/CardModal'
import CardEditionsGridOverlay from 'components/card/CardEditionsGridOverlay'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import CornerOwned from 'components/card/controlsAndInfo/cornerOwned'
import CompanionTag from 'components/card/controlsAndInfo/companionTag'
import BasicCard from 'components/card/BasicCard'
import DraggableBasicCard from 'components/card/DraggableBasicCard'
import Prices from 'components/card/controlsAndInfo/prices'
import Icon from 'components/elements/Icon'
import CornerQuantity from 'components/card/controlsAndInfo/cornerQuantity'
import ContextMenu from 'components/elements/ContextMenu'
import { NextPrevOptions } from 'components/elements/Overlay/CardDetailsOverlay'
import { StandaloneCardGridOverlay } from 'components/deckPage/modals/cardComparisonOverlay'

import { CardType, EDH_FORMATS, OWNED } from 'types/deck'

import styles from './searchedCard.module.scss'

type Props = {
  card: CardType
  onCardClicked?: (card: CardType, skipGoBackOnClick?: boolean) => void
  onCardUpdated?: (currentId: string, updatedCard: CardType) => void
  allowDragging?: boolean
  hidePrices?: boolean
  className?: string
  onDragStart?: () => void
  onDragStop?: () => void
  editionControls?: boolean
  cardIsSelected?: boolean
  nextPrevControls?: {
    previous: NextPrevOptions
    next: NextPrevOptions
  }
}

// The card that appears in the search grid (lightly tweaked UI among other options unique to search)
// This is used in both the normal grid and editions grid
const SearchedCard = memo(
  ({
    card,
    onCardClicked,
    onCardUpdated,
    allowDragging,
    hidePrices,
    className = '',
    onDragStart,
    onDragStop,
    editionControls,
    cardIsSelected,
    nextPrevControls,
  }: Props) => {
    const [{ tbId: username }] = useCookies(['tbId'])

    const isDeckPage = typeof window !== 'undefined' && location.pathname.startsWith('/decks/')

    const canEditDeck = useCanEditDeck()
    const format = useAppSelector(state => state.deck.format)
    const errorCard = useAppSelector(state => state.deck.warningCards[card.name])
    const categories = useAppSelector(state => state.deck.categories)

    const [setActiveState] = useActionless(SET_ACTIVE_STATE)

    const [localQuantity, setLocalQuantity] = useState(card.qty) // used while debouncing fast quantity changes
    const [flipped, setFlipped] = useState(card.flippedDefault)
    const [viewCardDetailsOverlay, setViewCardDetailsOverlay] = useState(false)
    const [viewEditionsOverlay, setViewEditionsOverlay] = useState(false)
    const [viewSimilarPrintingsInDeck, setViewSimilarPrintingsInDeck] = useState(false)

    const handleCardClick = (skipGoBackOnClick?: boolean) => {
      if (!onCardClicked) return

      onCardClicked(card, skipGoBackOnClick)
    }

    const handleMoveTo = (card: CardType, categoryName: string, keepOtherCategories = true) => {
      if (!onCardUpdated) return
      if (card.categories.includes(categoryName)) return

      const updatedCategories = keepOtherCategories ? [categoryName, ...card.categories] : [categoryName]

      onCardUpdated(card.id, { ...card, qty: card.qty || 1, categories: updatedCategories })
    }

    const handleHardAddNewEdition = (card: CardType) =>
      SaveService.save({ ...card, id: shortid.generate(), deckRelationId: '', qty: 1 })

    const handleSetDebouncedQty = (qty: number) => {
      if (onCardUpdated) onCardUpdated(card.id, { ...card, qty })
    }

    // Reset local state if the cards quantity changes someway that isn't here
    // Reset the debounce method to make sure it has correct card info (since it's only updated when lodash.debounce is called)
    const debouncedQtyChange = useMemo(() => {
      if (card.qty !== localQuantity) setLocalQuantity(card.qty)

      return debounce(handleSetDebouncedQty, 660)
    }, [card])

    const handleAddCard = () => {
      setLocalQuantity(localQuantity + 1)
      debouncedQtyChange(localQuantity + 1)
    }

    const handleRemoveCard = () => {
      setLocalQuantity(localQuantity - 1 > 0 ? localQuantity - 1 : 0)
      debouncedQtyChange(localQuantity - 1)
    }

    const handlePinCard = () => {
      if (card.pinnedStatus !== OWNED.NO)
        return setActiveState({ pinnedCardsOverlay: true, pinnedCardsFilteredCards: [card.name] })

      PinService.add(card)
        .then(() => {
          ToastService.create(`Pinned ${card.name}`, 'Pinned Cards', 'success')
          if (onCardUpdated) onCardUpdated(card.id, { ...card, pinnedStatus: OWNED.YES })
        })
        .catch(err => ToastService.create(err.error, 'Pinned Cards', 'error'))
    }

    const handleDragStart = () => {
      // if (isSearchLocked) return

      if (!onDragStart) return setActiveState({ hideStackOverlay: true })

      onDragStart()
    }

    const handleDragStop = () => {
      // if (isSearchLocked) return

      if (!onDragStop) return setActiveState({ hideStackOverlay: false })

      onDragStop()
    }

    const isCommanderFormat = EDH_FORMATS.includes(format)

    // prettier-ignore
    const addSide = (<><Icon name={card.qty ? 'arrow right' : 'add'} /> Sideboard</>)
    // prettier-ignore
    const addMaybe = (<><Icon name={card.qty ? 'arrow right' : 'add'} /> Maybeboard</>)
    // prettier-ignore
    const addCommander = (<><Icon name="chess queen" /> Set Commander</>)
    // prettier-ignore
    const viewMore = (<><Icon name="arrow right" /> Card Details</>)

    // prettier-ignore
    const switchToEdition = (<><Icon name="arrow right" /> Switch to edition</>)
    // prettier-ignore
    const addNewEdition = (<><Icon name="add" /> Add as new card</>)
    // prettier-ignore
    const allPrintings = (<><Icon name="grid layout" /> All printings</>)

    const options: Option[] =
      editionControls && isDeckPage
        ? [
            { label: switchToEdition, onClick: () => handleCardClick() },
            { label: addNewEdition, onClick: () => handleHardAddNewEdition(card) },
            { type: 'spacer' },
            {
              label: 'Select and goto next',
              onClick: () => {
                handleCardClick(true)

                if (nextPrevControls?.next.onClick) nextPrevControls.next.onClick()
              },
              icon: 'arrow circle right',
            },
            {
              label: 'Select and goto previous',
              onClick: () => {
                handleCardClick(true)

                if (nextPrevControls?.previous.onClick) nextPrevControls.previous.onClick()
              },
              icon: 'arrow circle left',
            },
          ]
        : [
            { label: addSide, onClick: () => handleMoveTo(card, 'Sideboard', !!card.qty) },
            { label: addMaybe, onClick: () => handleMoveTo(card, 'Maybeboard', !!card.qty) },
            {
              label: addCommander,
              onClick: () => handleMoveTo(card, 'Commander', false),
              hidden: !isCommanderFormat,
            },
            { type: 'spacer' },
            { label: 'Pin card', icon: 'pin', onClick: () => handlePinCard() },
            { type: 'spacer' },
            {
              label: 'Add to category',
              icon: 'arrow right',
              type: 'extras',
              id: 'add-to-other',
              className: styles.moveCategoryExtras,
              options: [
                ...Object.values(categories)
                  .map(category => ({
                    label: category.name,
                    onClick: () => handleMoveTo(card, category.name, !!card.qty),
                    hidden: ['Maybeboard', 'Sideboard', 'Commander'].includes(category.name),
                  }))
                  .sort((a, b) => (a.label > b.label ? 1 : -1)),
              ],
            },
            { type: 'spacer' },
            { label: addNewEdition, onClick: () => handleHardAddNewEdition(card), hidden: !card.qty },
            { label: viewMore, onClick: () => setViewCardDetailsOverlay(true) },
            { label: allPrintings, onClick: () => setViewEditionsOverlay(true) },
          ]

    return (
      <>
        <ContextMenu options={options} className={className} menuClassName={styles.contextMenu}>
          <div className={styles.container} title={`${card.name} (${card.setCode}) ${card.collectorNumber}`}>
            {!!card.totalPrintingQtyInDeck && !editionControls && (
              <button
                className={card.qty ? styles.inDeckButton : styles.otherPrintingsButton}
                onClick={() =>
                  card.relatedCardIds.length > 1 || card.relatedCardIds[0] !== card.id
                    ? setViewSimilarPrintingsInDeck(true)
                    : setViewCardDetailsOverlay(true)
                }>
                Copies: {card.qty}
                {card.totalPrintingQtyInDeck !== card.qty && (
                  <>
                    {' '}
                    (
                    <span className={styles.othersHoverIndicator}>
                      +{card.totalPrintingQtyInDeck - card.qty} others
                    </span>
                    )
                  </>
                )}
              </button>
            )}

            {editionControls && isDeckPage && cardIsSelected && (
              <button className={styles.currentlySelectedCardBorder}>
                {/* <Icon name="check circle" /> */}
                Selected printing
              </button>
            )}

            <div className={styles.card}>
              <button
                className={`${styles.cardButton} ${onCardClicked ? styles.clickableCard : ''}`}
                onClick={() => handleCardClick()}>
                {isDeckPage && allowDragging ? (
                  <DraggableBasicCard
                    card={card}
                    className={styles.basicCard}
                    imageClassName={styles.cardImage}
                    error={errorCard}
                    flipped={flipped}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragStop}
                  />
                ) : (
                  <BasicCard
                    forceOnlyShowFaceUpSide
                    className={styles.basicCard}
                    imageClassName={styles.cardImage}
                    error={errorCard}
                    card={card}
                    flipped={flipped}
                  />
                )}
              </button>

              {/* Corners */}
              {!!localQuantity && !editionControls && (
                <CornerQuantity
                  card={{ ...card, qty: localQuantity }}
                  onCardUpdate={updatedCard => onCardUpdated && onCardUpdated(updatedCard.id, updatedCard)}
                />
              )}

              {!!username && <CornerOwned owned={card.owned} />}
              <CompanionTag card={card} />

              {onCardUpdated && canEditDeck && !editionControls && (
                <div className={styles.controls}>
                  <button className={styles.increaseButton} onClick={handleAddCard}>
                    <Icon name="add" />
                  </button>
                  <button disabled={!localQuantity} className={styles.decreaseButton} onClick={handleRemoveCard}>
                    <Icon name="minus" />
                  </button>

                  <button className={styles.cardDetailsButton} onClick={() => setViewCardDetailsOverlay(true)}>
                    <Icon name="chevron right" />
                  </button>
                </div>
              )}

              <div className={styles.leftButtonContainer}>
                {card.pinnedStatus !== OWNED.NO && (
                  <button
                    className={card.pinnedStatus === OWNED.YES ? styles.exactInClipboard : styles.printingInClipboard}
                    onClick={handlePinCard}>
                    <Icon name="pin" />
                  </button>
                )}

                {DOUBLE_FACED_LAYOUTS.includes(card.layout) && (
                  <button onClick={() => setFlipped(!flipped)}>
                    <Icon name="refresh" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {!hidePrices && (
            <div className={styles.actions}>
              <Prices card={card} />

              <ArchidektDropdown menuClassName={styles.dropdown} options={options}>
                <button className={styles.extrasTrigger}>
                  <Icon name="ellipsis horizontal" />
                </button>
              </ArchidektDropdown>
            </div>
          )}
        </ContextMenu>

        {!editionControls && (
          <>
            <CardModalStandalone
              open={viewCardDetailsOverlay}
              card={card}
              onCardUpdated={updatedCard => onCardUpdated && onCardUpdated(card.id, updatedCard)}
              onClose={() => setViewCardDetailsOverlay(false)}
            />

            <CardEditionsGridOverlay
              open={viewEditionsOverlay}
              card={card}
              onCardSelected={updatedCard => {
                setViewEditionsOverlay(false)

                if (onCardUpdated) setTimeout(() => onCardUpdated(card.id, updatedCard), 150) // prevent flashing
              }}
              onClose={() => setViewEditionsOverlay(false)}
            />

            <StandaloneCardGridOverlay
              header="Matching cards (already in deck)"
              open={viewSimilarPrintingsInDeck}
              onClose={() => setViewSimilarPrintingsInDeck(false)}
              cardIds={card.relatedCardIds}
            />
          </>
        )}
      </>
    )
  },
  (prevProps: Props, nextProps: Props) => {
    // Only ever rerender this component if and only if the card has changed
    return isEqual(prevProps.card, nextProps.card)
  },
)

export default SearchedCard
